import React, { useEffect, useReducer, useRef, useState } from 'react';
import requiredIf from 'react-required-if';
import { toast } from 'react-toastify';
import PropTypes from 'prop-types';
import SearchIcon from '@mui/icons-material/Search';
import axios from 'axios';
import { Formik, useFormikContext } from 'formik';
import { capitalize, omit, pick } from 'lodash';
import * as Yup from 'yup';

import Button from '~/components/core/Atomic/Buttons/Button';
import Chip from '~/components/core/Atomic/Chip/Chip';
import CircularProgress from '~/components/core/Atomic/CircularProgress/CircularProgress';
import Grid from '~/components/core/Atomic/Grid/Grid';
import InputAdornment from '~/components/core/Atomic/InputAdornment';
import MenuItem from '~/components/core/Atomic/MenuItem';
import Typography from '~/components/core/Atomic/Typography';
import CancelButton from '~/components/core/Buttons/CancelButton';
import CollapsibleWrapper from '~/components/core/Collapsible/CollapsibleWrapper';
import OverflowArrayTextDisplayWithTooltip from '~/components/core/OverflowArrayTextWithTooltip/OverflowArrayTextDisplayWithTooltip';
import { useLobConfiguration } from '~/components/hooks/useLobConfiguration';
import { isFutureDateOfLossAllowed } from '~/components/IncidentConfiguration/utils';
import POLICY_SEARCH_TYPES from '~/server_shared/generated-types/POLICY_SEARCH_TYPES';
import cn from '~/Utils/cn';

import { isoDateToUs } from '../DateTimeUtils';
import { policyPopulate } from '../PolicyUtils';
import { CONFIGURATION_FEATURES_NAMES, CONSTANTS } from '../Types';
import { isFeatureEnabled, reportAxiosError, reportErrorInProductionOrThrow } from '../Utils';
import { getLobDescription } from '../Utils/lobUtils';

import { FsButton } from './core/FsWrappers';
import { FnolDraftsTable } from './Fnol/FnolDraft';
import { useCms } from './hooks/useCms';
import useGlobalAddresses from './hooks/useGlobalAddresses';
import { LobSelectFormik } from './TPA/LOB/LobSelectFormik';
import SubOrganizationSelectFormik from './TPA/SubOrganizations/SubOrganizationSelectFormik';
import CardDialog from './CardDialog';
import { ClaimSearchResults } from './ClaimSearch';
import { PERMISSION_ACTIONS, PERMISSION_VERBS, RestrictedPermissions } from './core';
import { getLocationFields, LocationFormikInner } from './Location';
import useOrganization from './OrganizationContext';
import PlainTable from './PlainTable';
import { hasPolicySource } from './policySearchSource';
import PolicySearchV2, { PolicySearchResultsV2 } from './PolicySearchV2';
import SearchableSubTable from './SearchableSubTable';
import { DatePickerTextFieldFormik, TextFieldFormik } from './TextFieldFormik';

import { useStyles } from '../assets/styles';
import styles from './policySearch.module.scss';

const spacing = 1;

function useAllowFutureDateOfLoss() {
  const { userOrganization } = useCms();
  const { subOrganizationEnabled, subOrganizations } = useOrganization();
  const { values } = useFormikContext();
  const selectedLob = values.lob;
  const selectedSubOrgId = values.sub_org_id;

  if (!selectedLob) {
    return false;
  }

  let lobIncidentConfigurationsDict;
  if (subOrganizationEnabled && selectedSubOrgId) {
    const subOrg = subOrganizations?.find((s) => s.id === selectedSubOrgId);
    lobIncidentConfigurationsDict = subOrg?.lob_incident_configurations_dict;
  } else if (!subOrganizationEnabled) {
    lobIncidentConfigurationsDict = userOrganization?.lob_incident_configurations_dict;
  }

  return isFutureDateOfLossAllowed(lobIncidentConfigurationsDict?.[selectedLob]);
}

function PolicySearchInner(props) {
  const {
    constSearchValues,
    onSearchByAddress,
    policySearchConfig,
    disabled,
    errorMsg,
    isSearchInProgress,
    hideSubOrg = false,
    hideDateOfLoss = false,
    hideLob = false,
    newDesign = false,
    onSetManualPolicy,
    includedSubOrganizationsIds,
    hideCollapsibleWrapper = false,
  } = props;
  const { handleSubmit, values, errors, isSubmitting } = useFormikContext();
  const { classes } = useStyles();
  const { subOrganizationEnabled, supportedClaimTypes } = useOrganization();
  const allowFutureDateOfLoss = useAllowFutureDateOfLoss();
  const policySearchMethod = policySearchConfig.method;
  const policyApiConfiguration = policySearchConfig.policy_api_configuration;
  const isSearchByAddressSupported = policySearchConfig.allow_search_by_address && onSearchByAddress;
  const shouldAskDateOfLoss = policySearchConfig.should_ask_date_of_loss && !hideDateOfLoss;
  const shouldAskLob = supportedClaimTypes.length > 1 && !hideLob;
  const shouldAskSubOrg = subOrganizationEnabled && !hideSubOrg;
  const endAdornment = (
    <InputAdornment position="end">
      {isSearchInProgress ? <CircularProgress size={32} /> : null}
      {newDesign ? <SearchIcon /> : null}
    </InputAdornment>
  );

  let SearchFields;
  if (policySearchMethod === 'DB') {
    SearchFields = (
      <Grid item xs>
        <TextFieldFormik
          id="search"
          className={classes.textField}
          InputProps={{
            placeholder: 'Search for policy by: insured name, phone number or policy number',
            endAdornment,
          }}
          disabled={isSubmitting || disabled || !!constSearchValues?.search}
          fullWidth
        />
      </Grid>
    );
  } else if (policySearchMethod === 'API') {
    if (!policySearchConfig.search_criteria) {
      SearchFields = (
        <Grid item xs>
          <TextFieldFormik
            id="search"
            className={classes.textField}
            InputProps={{
              endAdornment,
            }}
            label={
              (policyApiConfiguration?.enabled &&
                (policyApiConfiguration?.policy_search_component_description || 'Search query')) ||
              'Policy Number'
            }
            disabled={isSubmitting || disabled || !!constSearchValues?.search}
            fullWidth
            error={!!errorMsg || !!errors.search}
            helperText={errorMsg || errors.search}
          />
        </Grid>
      );
    } else {
      SearchFields = (
        <>
          {policySearchConfig.search_criteria && (
            <Grid item xs>
              <TextFieldFormik
                id="search_criteria"
                className={classes.textField}
                label="Search By"
                disabled={isSubmitting || disabled || !!constSearchValues?.search}
                fullWidth
                select
              >
                {Object.keys(policySearchConfig.search_criteria).map((option) => (
                  <MenuItem key={option} value={option}>
                    {policySearchConfig.search_criteria[option].desc}
                  </MenuItem>
                ))}
              </TextFieldFormik>
            </Grid>
          )}
          <Grid item xs>
            <TextFieldFormik
              id="search"
              className={classes.textField}
              InputProps={{
                endAdornment,
              }}
              label={
                values['search_criteria'] ? policySearchConfig.search_criteria[values['search_criteria']].desc : ' '
              }
              disabled={isSubmitting || disabled || !!constSearchValues?.search || !values['search_criteria']}
              fullWidth
              error={!!errorMsg} // this will override the formik error - but we currently have not validation on search
              helperText={errorMsg}
            />
          </Grid>
        </>
      );
    }
  }
  // No `else` reporting an unknown method: `PolicySearchContainer` is the only route to this
  // component, and it now reports and returns before rendering anything it cannot search.
  const isSubOrgOrLobShown = (shouldAskSubOrg || shouldAskLob) && policyApiConfiguration?.enabled;
  const isSubOrgSelectDisabled = disabled || isSubmitting || !!constSearchValues?.sub_org_id;
  const lobSelectFieldSubOrgIds = [values.sub_org_id || constSearchValues?.sub_org_id].filter(Boolean);
  const isLobSelectFieldDisabled = disabled || isSubmitting || !!constSearchValues?.lob;
  const formContent = (
    <form onSubmit={(e) => e.preventDefault()}>
      <div className={classes.containerCentered}>
        <Grid container spacing={2}>
          {policySearchMethod === 'API' && policyApiConfiguration?.enabled && (
            <>
              {shouldAskSubOrg && (
                <Grid item xs={2}>
                  <SubOrganizationSelectFormik
                    fieldId="sub_org_id"
                    disabled={isSubOrgSelectDisabled}
                    includedSubOrganizationsIds={includedSubOrganizationsIds}
                  />
                </Grid>
              )}
              {shouldAskLob && (
                <Grid item xs={2}>
                  <LobSelectFormik
                    fieldId="lob"
                    subOrganizationIds={lobSelectFieldSubOrgIds}
                    disabled={isLobSelectFieldDisabled}
                  />
                </Grid>
              )}
            </>
          )}
          {SearchFields}
          {shouldAskDateOfLoss ? (
            <Grid item xs={isSubOrgOrLobShown ? 3 : 4}>
              <DatePickerTextFieldFormik
                id="date_of_loss"
                label="Date"
                disableFuture={!allowFutureDateOfLoss}
                disabled={isSubmitting || disabled || !!constSearchValues?.date_of_loss}
                className={cn(classes.textField, 'pe-8')}
                fullWidth
              />
            </Grid>
          ) : null}
        </Grid>
        {!newDesign && (
          <>
            <Button
              onClick={handleSubmit}
              className={classes.button}
              disabled={isSearchInProgress || disabled}
              variant="contained"
              color="primary"
            >
              Search
            </Button>
            {isSearchByAddressSupported && (
              <div>
                <Button
                  style={{ width: '160px' }}
                  className={classes.button}
                  onClick={onSearchByAddress}
                  disabled={isSearchInProgress || disabled}
                  variant="contained"
                  size="small"
                  color="default"
                >
                  Search by address
                </Button>
              </div>
            )}
          </>
        )}
      </div>
      {newDesign && (
        <div className={styles.btnContainer}>
          <Button
            onClick={handleSubmit}
            className={styles.btn}
            disabled={isSearchInProgress || disabled}
            variant="contained"
            color="primary"
          >
            Search
          </Button>
          {onSetManualPolicy && (
            <RestrictedPermissions action={PERMISSION_ACTIONS.MANUAL_POLICY} verb={PERMISSION_VERBS.WRITE}>
              <CancelButton
                classNameOverride={cn(styles.btn, styles.setPolicyBtn)}
                onClick={onSetManualPolicy}
                content="Set Policy Details Manually"
              />
            </RestrictedPermissions>
          )}
        </div>
      )}
    </form>
  );

  if (hideCollapsibleWrapper) {
    return formContent;
  }

  return (
    <CollapsibleWrapper
      title="Search Policy"
      actionCard
      noBorder
      backgroundColor={CollapsibleWrapper.BACKGROUND_COLOR.WHITE}
      defaultState
    >
      {formContent}
    </CollapsibleWrapper>
  );
}

PolicySearchInner.propTypes = {
  constSearchValues: PropTypes.object,
  onSearch: PropTypes.func.isRequired,
  onSearchByAddress: PropTypes.func.isRequired,
  policySearchConfig: PropTypes.object.isRequired,
  onCouldntFindPolicy: PropTypes.func,
  policyTypes: PropTypes.array.isRequired,
  disabled: PropTypes.bool,
  errorMsg: PropTypes.string,
  isSearchInProgress: PropTypes.bool,
  hideSubOrg: PropTypes.bool,
  hideDateOfLoss: PropTypes.bool,
  hideLob: PropTypes.bool,
  newDesign: PropTypes.bool,
  onSetManualPolicy: PropTypes.func,
  includedSubOrganizationsIds: PropTypes.array,
  hideCollapsibleWrapper: PropTypes.bool,
};

PolicySearchInner.defaultProps = {
  constSearchValues: {},
};

function PolicySearch(props) {
  const {
    constSearchValues,
    initialSearchValues,
    onSearch,
    policySearchConfig,
    hideSubOrg = false,
    hideDateOfLoss = false,
    hideLob = false,
  } = props;
  const { userOrganization } = useCms();
  const { subOrganizationEnabled, subOrganizations, supportedClaimTypes } = useOrganization();
  const shouldAskDateOfLoss = policySearchConfig.should_ask_date_of_loss && !hideDateOfLoss;
  const shouldAskLob = supportedClaimTypes.length > 1 && !hideLob;
  const shouldAskSubOrg = subOrganizationEnabled && !hideSubOrg;
  const shouldRequireSubOrg = shouldAskSubOrg && policySearchConfig.should_require_sub_org;
  const shouldRequirePolicyNumber = policySearchConfig.required_policy_number;

  return (
    <Formik
      initialValues={{
        search_criteria: '',
        search: '',
        date_of_loss: '',
        sub_org_id: '',
        lob: shouldAskLob ? '' : supportedClaimTypes[0],
        reported_date: '',
        // Seeds only, unlike constSearchValues: the fields stay editable and enabled. The
        // date-of-loss seed is dropped when the input is not rendered, so the search (and the
        // related-claims filter it feeds) is never narrowed by a date the adjuster can neither
        // see nor clear.
        ...(shouldAskDateOfLoss ? initialSearchValues : omit(initialSearchValues, ['date_of_loss'])),
        ...constSearchValues,
      }}
      validationSchema={Yup.object().shape({
        search_criteria: policySearchConfig.search_criteria ? Yup.string().required('Required') : undefined,
        search: shouldRequirePolicyNumber ? Yup.string().required('Required') : undefined,
        sub_org_id: !shouldAskSubOrg
          ? undefined
          : shouldRequireSubOrg
            ? Yup.string().required('Required')
            : Yup.string(),
        lob: shouldAskLob && shouldRequireSubOrg ? Yup.string().required('Required') : Yup.string(),
        date_of_loss: Yup.date()
          .nullable()
          .test('is-required', 'Required', (value) => {
            if (!shouldAskDateOfLoss) {
              return true;
            }
            return !!value;
          })
          .test('not-in-future', 'Date of loss cannot be in the future', function (value) {
            if (!value) return true;
            const { lob, sub_org_id } = this.parent;
            let lobConfig;
            if (subOrganizationEnabled && sub_org_id) {
              const subOrg = subOrganizations?.find((s) => String(s.id) === String(sub_org_id));
              lobConfig = subOrg?.lob_incident_configurations_dict?.[lob];
            } else if (!subOrganizationEnabled) {
              lobConfig = userOrganization?.lob_incident_configurations_dict?.[lob];
            }
            if (isFutureDateOfLossAllowed(lobConfig)) return true;
            return value <= new Date();
          }),
      })}
      validateOnChange={false}
      validateOnBlur={false}
      onSubmit={async (values, formikProps) => {
        try {
          await onSearch(values);
        } catch {
          formikProps.setSubmitting(false);
        }
      }}
    >
      <PolicySearchInner {...props} />
    </Formik>
  );
}

PolicySearch.propTypes = {
  constSearchValues: PropTypes.object,
  initialSearchValues: PropTypes.object,
  onSearch: PropTypes.func.isRequired,
  onSearchByAddress: PropTypes.func.isRequired,
  policySearchConfig: PropTypes.object.isRequired,
  onCouldntFindPolicy: PropTypes.func,
  policyTypes: PropTypes.array.isRequired,
  disabled: PropTypes.bool,
  errorMsg: PropTypes.string,
  isSearchInProgress: PropTypes.bool,
  hideSubOrg: PropTypes.bool,
  hideDateOfLoss: PropTypes.bool,
  hideLob: PropTypes.bool,
  newDesign: PropTypes.bool,
  onSetManualPolicy: PropTypes.func,
  includedSubOrganizationsIds: PropTypes.array,
  hideCollapsibleWrapper: PropTypes.bool,
};

PolicySearch.defaultProps = {
  constSearchValues: {},
};

function policyTypeToDisplay(policyType, lobConfigurationsDict) {
  const lob = policyType.replace('_policy', '_claim');
  return getLobDescription(lob, lobConfigurationsDict);
}

// Exported for unit tests so the regression for the 200-with-`missing` contract is covered
// without booting the whole PolicySearchContainer integration. Returns one of:
//   { status: 'ok', missing: [...] }  — pre-check completed; non-empty `missing` means abort
//   { status: 'rejected' }            — endpoint returned 400 (genuine validation rejection)
//   { status: 'transient' }           — 5xx / network — fall through to submit-time gate
export async function checkPolicyPreDefinedRequiredFields({ policyData, subOrgId }) {
  try {
    const { data } = await axios.post('/api/v1/policies/validate_pre_defined_required_fields', {
      policy_data: policyData,
      lob: policyData?.lob ?? policyData?.type,
      sub_org_id: subOrgId,
    });
    return { status: 'ok', missing: data?.missing ?? [] };
  } catch (error) {
    if (error?.response?.status === 400) {
      reportAxiosError(error);
      return { status: 'rejected' };
    }
    return { status: 'transient' };
  }
}

export function PolicySearchResults(props) {
  const {
    classes,
    searchPoliciesResults,
    onCancel,
    onSelectPolicy,
    policyTypes,
    selectButtonText,
    disableSelectButton,
    isDialog = true,
    noCardTitle,
    onTakeLossWithoutPolicy,
  } = props;

  const [isPolicySelectInProgress, setIsPolicySelectInProgress] = React.useState(false);
  const { userOrganization } = useCms();

  const showMultipleTypes = policyTypes.length > 1;
  const { policySearchConfig } = useOrganization();
  const { lobConfigurationsDict } = useLobConfiguration();
  const policyApiConfiguration = policySearchConfig.policy_api_configuration;
  const isCommercialPoliciesEnabled = isFeatureEnabled(
    userOrganization,
    CONFIGURATION_FEATURES_NAMES.COMMERCIAL_POLICIES
  );

  async function handleSelectPolicy(row) {
    setIsPolicySelectInProgress(true);
    await onSelectPolicy(row);
    setIsPolicySelectInProgress(false);
  }

  function getPolicyColumnData(policyType) {
    let columns = [
      { id: 'policy_number', numeric: false, label: 'Policy #', nowrap: true },
      {
        id: 'effective_time',
        numeric: false,
        label: 'Effective Date',
        specialCell: (row) => isoDateToUs(row.policy_effective_datetime),
      },
      {
        id: 'expiration_time',
        numeric: false,
        label: 'Expiration Date',
        specialCell: (row) => isoDateToUs(row.policy_expiration_datetime),
      },
      {
        id: 'insured_contact_full_name',
        numeric: false,
        label: 'Insured Name',
        specialCell: (row) =>
          row.insured_contact_full_name || (row.insured_contact ? row.insured_contact.full_name : ''),
      },
    ];
    const lob = policyType.replace('_policy', '_claim');
    if (lobConfigurationsDict?.[lob]) {
      // Generate dynamic covered object display name with max 3 descriptions
      const getCoveredObjectDisplayName = (lobConfig) => {
        const coveredObjects = lobConfig?.covered_objects || [];

        if (coveredObjects.length === 0) return 'Covered Objects';
        if (coveredObjects.length === 1) return coveredObjects[0].desc;
        if (coveredObjects.length <= 3) {
          return coveredObjects.map((obj) => obj.desc).join(' / ');
        }

        return 'Covered Objects';
      };

      const coveredObjectDisplayName = getCoveredObjectDisplayName(lobConfigurationsDict[lob]);

      columns = columns.concat([
        {
          id: 'insured_phone_number',
          numeric: false,
          label: 'Phone #',
          specialCell: (row) => (row.insured_contact.phones.length > 0 ? row.insured_contact.phones[0].phone : ''),
        },
        { id: 'insured_address', numeric: false, label: 'Insured Address' },
        {
          id: 'covered_object_name_chips',
          numeric: false,
          label: coveredObjectDisplayName,
          isHidden: !isCommercialPoliciesEnabled,
          specialCell: (row) => (
            <div>
              <OverflowArrayTextDisplayWithTooltip
                value={row?.display_names?.map((coveredObject) => coveredObject?.display_name)}
                maxTooltipItems={5}
                renderItem={(label) => (
                  <span key={label}>
                    <Chip label={label} size="small" className="mr-12" />
                  </span>
                )}
              />
            </div>
          ),
          disableSort: true,
        },
        {
          id: 'covered_object_name',
          numeric: false,
          label: coveredObjectDisplayName,
          expandable: true,
          specialCell: (row) => {
            if (!isCommercialPoliciesEnabled) {
              return (
                <>
                  {policyApiConfiguration?.enabled
                    ? row.display_names.map((coveredObject) => (
                        <div key={coveredObject.id}>{coveredObject.display_name}</div>
                      ))
                    : ''}
                </>
              );
            }
            const subColumns = [{ id: 'display_name', numeric: false, label: 'Name' }];
            if (row?.display_names?.[0]?.owner) {
              subColumns.push({ id: 'owner', numeric: false, label: 'Owner' });
            }
            const additionalFields = row?.display_names?.[0]?.additional_fields;
            if (additionalFields) {
              Object.keys(additionalFields)
                ?.slice(0, 3)
                ?.forEach((key) => {
                  subColumns.push({
                    id: key,
                    numeric: false,
                    label: capitalize(key)?.replaceAll('_', ' '),
                    // Strings render as-is; JSON.stringify would wrap them in literal quotes. Other
                    // types (objects, arrays, booleans, null) still stringify to something readable.
                    specialCell: (subRow) => {
                      const value = subRow?.[key];
                      return typeof value === 'string' ? value : JSON.stringify(value);
                    },
                  });
                });
            }

            const rows = row.display_names?.map((coveredObject) => ({
              ...coveredObject,
              ...coveredObject?.additional_fields,
            }));

            return <SearchableSubTable columns={subColumns} rows={rows} searchPlaceholder="Search covered objects" />;
          },
        },
      ]);
    } else {
      switch (policyType) {
        case 'home_policy':
          columns = columns.concat([
            {
              id: 'insured_phone_number',
              numeric: false,
              label: 'Phone #',
              specialCell: (row) =>
                policyApiConfiguration?.enabled ? row.insured_contact.phones[0]?.phone : row.insured_phone_number,
            },
            {
              id: 'insured_property_full_address',
              numeric: false,
              label: 'Insured Property Address',
              specialCell: (row) =>
                policyApiConfiguration?.enabled ? (
                  row.display_names.map((home) => <div key={home.id}>{home.display_name}</div>)
                ) : (
                  <div key={row.id}>{row.insured_property_full_address}</div>
                ),
            },
          ]);
          break;
        case 'auto_policy':
          columns = columns.concat([
            {
              id: 'insured_phone_number',
              numeric: false,
              label: 'Phone #',
              specialCell: (row) => (row.insured_contact.phones.length > 0 ? row.insured_contact.phones[0].phone : ''),
            },
            { id: 'insured_address', numeric: false, label: 'Insured Address' },
            {
              id: 'vehicle_display_name_chips',
              label: 'Covered Vehicles',
              isHidden: !isCommercialPoliciesEnabled,
              specialCell: (row) => (
                <div>
                  <OverflowArrayTextDisplayWithTooltip
                    value={row?.display_names?.map((vehicle) => vehicle?.display_name)}
                    maxTooltipItems={5}
                    renderItem={(label) => (
                      <span key={label}>
                        <Chip label={label} size="small" className="mr-12" />
                      </span>
                    )}
                  />
                </div>
              ),
              disableSort: true,
            },
            {
              id: 'vehicle_display_name',
              numeric: false,
              label: 'Vehicles',
              expandable: isCommercialPoliciesEnabled,
              specialCell: (row) => {
                if (!isCommercialPoliciesEnabled) {
                  return (
                    <>
                      {policyApiConfiguration?.enabled
                        ? row.display_names.map((vehicle) => <div key={vehicle.id}>{vehicle.display_name}</div>)
                        : row.covered_vehicles.map((vehicle) => (
                            <div key={vehicle.id}>{vehicle.vehicle.display_name}</div>
                          ))}
                    </>
                  );
                }
                const subColumns = [
                  { id: 'year', numeric: false, label: 'Year' },
                  { id: 'make', numeric: false, label: 'Make' },
                  { id: 'model', numeric: false, label: 'Model' },
                  { id: 'series', numeric: false, label: 'Series' },
                  { id: 'vin', numeric: false, label: 'VIN' },
                  { id: 'plate_number', numeric: false, label: 'Plate Number' },
                  { id: 'vehicle_plate_state', numeric: false, label: 'Registration State' },
                  { id: 'vehicle_owner_contact_full_name', numeric: false, label: 'Owner' },
                ];

                const rows = policyApiConfiguration?.enabled
                  ? row.display_names
                  : row.covered_vehicles?.map((cv) => cv?.vehicle);

                return <SearchableSubTable columns={subColumns} rows={rows} searchPlaceholder="Search vehicles" />;
              },
            },
          ]);
          break;
        case 'pet_policy':
          columns = columns.concat([
            {
              id: 'insured_phone_number',
              numeric: false,
              label: 'Phone #',
              specialCell: (row) => (row.insured_contact.phones.length > 0 ? row.insured_contact.phones[0].phone : ''),
            },
            { id: 'insured_address', numeric: false, label: 'Insured Address' },
            // eslint-disable-next-line react/jsx-key
            {
              id: 'pet_name',
              numeric: false,
              label: 'Pet Name',
              specialCell: (row) =>
                policyApiConfiguration?.enabled
                  ? row.display_names.map((coveredPet) => <div key={coveredPet.id}>{coveredPet.display_name}</div>)
                  : row.covered_pets.map(({ pet }) => <div key={pet.id}>{pet.display_name}</div>),
            },
          ]);
          break;
        default:
          break;
      }
    }

    // So these columns always appears last
    columns = columns.concat([
      {
        id: 'select_button',
        numeric: false,
        label: '',
        // eslint-disable-next-line react/display-name
        specialCell: (row) => (
          <FsButton
            color="primary"
            disabled={disableSelectButton || isPolicySelectInProgress}
            onClick={() => handleSelectPolicy(row)}
          >
            {selectButtonText || 'Select'}
          </FsButton>
        ),
      },
    ]);

    return columns;
  }

  return (
    <CardDialog
      title="Search Policy"
      isDialog={isDialog}
      noCardTitle={noCardTitle}
      onClose={onCancel}
      maxWidth={isCommercialPoliciesEnabled ? 'lg' : 'md'}
      fullWidth
      preventClose={isPolicySelectInProgress}
    >
      {policyTypes.map((policyType) => {
        const searchPolicyResultsForType = searchPoliciesResults[policyType];

        return (
          <React.Fragment key={policyType}>
            {searchPolicyResultsForType.length > 0 && (
              <Grid container spacing={spacing} style={{ marginTop: '8px' }}>
                <Grid item xs={12}>
                  {showMultipleTypes && (
                    <Typography display="block" variant="subtitle1" style={{ marginBottom: '6px' }}>
                      {policyTypeToDisplay(policyType, lobConfigurationsDict)}
                    </Typography>
                  )}
                  <PlainTable
                    classes={classes}
                    columns={getPolicyColumnData(policyType)}
                    rows={searchPolicyResultsForType}
                    shouldRenderExpandAsTable={isCommercialPoliciesEnabled}
                  />
                </Grid>
              </Grid>
            )}
            {searchPolicyResultsForType.length === 10 && (
              <Typography variant="subtitle2" style={{ marginBottom: '6px' }}>
                Showing only the first 10 results. Refine your search query to find other policies
              </Typography>
            )}
          </React.Fragment>
        );
      })}
      {onTakeLossWithoutPolicy && (
        <div className="mt-16 flex justify-end gap-8">
          <FsButton variant="outlined" onClick={onCancel}>
            Cancel
          </FsButton>
          <FsButton variant="contained" color="primary" onClick={onTakeLossWithoutPolicy}>
            Take Loss Without Policy
          </FsButton>
        </div>
      )}
    </CardDialog>
  );
}

PolicySearchResults.propTypes = {
  classes: PropTypes.object.isRequired,
  searchPoliciesResults: PropTypes.object.isRequired,
  onSelectPolicy: PropTypes.func.isRequired,
  onCouldntFindPolicy: PropTypes.func,
  policyTypes: PropTypes.array.isRequired,
  selectButtonText: PropTypes.string,
  disableSelectButton: PropTypes.bool,
  onCancel: PropTypes.func,
  onTakeLossWithoutPolicy: PropTypes.func,
  isDialog: PropTypes.bool,
  noCardTitle: PropTypes.bool,
};

function SearchByAddressDialog(props) {
  const { policySearchConfig, initialSearchValues, onSearch, onClose } = props;
  const { classes } = useStyles();

  const { defaultCountry } = useGlobalAddresses({ countriesConfigurationKey: 'loss_location' });

  const shouldAskDateOfLoss = policySearchConfig.should_ask_date_of_loss;
  return (
    <Formik
      initialValues={{
        ...getLocationFields(defaultCountry),
        // The same seed the main search form gets, so this route doesn't make the adjuster
        // re-type a date the caller already knows. Narrowed to the one seeded field this dialog
        // renders: every value here rides into the by-address search request.
        ...(shouldAskDateOfLoss ? pick(initialSearchValues, ['date_of_loss']) : {}),
      }}
      validationSchema={Yup.object().shape({
        address1: Yup.string().required('Required'),
        date_of_loss: shouldAskDateOfLoss ? Yup.date().required('Required') : undefined,
      })}
      onSubmit={async (values, formikProps) => {
        try {
          await onSearch(values);
          await onClose(values);
        } catch {
          formikProps.setSubmitting(false);
        }
      }}
      enableReinitialize
    >
      {(formikProps) => (
        <CardDialog title="Search Policy by Address" isDialog={true} onClose={onClose} maxWidth="sm" fullWidth>
          {shouldAskDateOfLoss ? (
            <DatePickerTextFieldFormik
              id="date_of_loss"
              label="Date of Loss"
              disabled={formikProps.isSubmitting}
              className={classes.textField}
              fullWidth
              disableFuture
            />
          ) : null}
          <LocationFormikInner
            classes={classes}
            submitText="Search"
            onCancel={onClose}
            {...formikProps}
            countriesConfigurationKey="loss_location"
          />
        </CardDialog>
      )}
    </Formik>
  );
}

SearchByAddressDialog.propTypes = {
  policySearchConfig: PropTypes.object.isRequired,
  initialSearchValues: PropTypes.object,
  onSearch: PropTypes.func.isRequired,
  onClose: PropTypes.func.isRequired,
};

const POLICY_SEARCH_INITIAL_VALUES = { search: '', date_of_loss: '', reported_date: '' };

function PolicySearchContainer(props) {
  const {
    enableSelectingRelatedClaim,
    constSearchValues,
    initialSearchValues,
    onSelectPolicy,
    onPolicyNotFound,
    disabled,
    policyTypes,
    selectButtonText,
    disableSelectButton,
    choosePolicyFromRelatedClaimsText,
    selectRelatedClaimComponent,
    onFnolDraftSelected,
    allowSearchByAddress,
    hideSubOrg = false,
    hideDateOfLoss = false,
    hideLob = false,
    newDesign = false,
    onSetManualPolicy,
    allowedSubOrgIds,
    returnOriginalSearchResult = false,
    hideCollapsibleWrapper = false,
    onTakeLossWithoutPolicy,
  } = props;
  const isMounted = useRef();
  const { userOrganization } = useCms();

  useEffect(() => {
    isMounted.current = true;
    return () => (isMounted.current = false);
  }, []);

  const initialState = {
    isSearchInProgress: false,
    searchResultsDict: undefined,
    searchValues: POLICY_SEARCH_INITIAL_VALUES,
  };

  const [searchResultsState, dispatch] = useReducer(reducer, initialState);
  const { isSearchInProgress, searchResultsDict, searchValues } = searchResultsState;
  const [showSearchByAddressDialog, setShowSearchByAddressDialog] = useState(false);
  const [relatedClaims, setRelatedClaims] = useState([]);
  const [relatedDrafts, setRelatedDrafts] = useState(undefined);
  const [selectedPolicyContext, setSelectedPolicyContext] = useState(undefined);
  const { policySearchConfig, subOrganizationEnabled, isPolicyAPIEnabled } = useOrganization();
  const [searchResultsLob, setSearchResultsLob] = useState([]);
  const [reltadClaimsSubmitting, setReltadClaimsSubmitting] = useState(false);
  const { classes } = useStyles();
  const isCommercialPoliciesEnabled = isFeatureEnabled(
    userOrganization,
    CONFIGURATION_FEATURES_NAMES.COMMERCIAL_POLICIES
  );
  const isConfigurableFnolEnabled = isFeatureEnabled(userOrganization, CONFIGURATION_FEATURES_NAMES.CONFIGURABLE_FNOL);
  const isPolicySearchV2Enabled =
    policySearchConfig?.method === 'API' && policySearchConfig?.policy_api_configuration?.version === 2;

  // Use allowedSubOrgIds directly with the new includedSubOrganizationsIds prop
  const includedSubOrganizationsIds = React.useMemo(() => {
    if (!allowedSubOrgIds || !subOrganizationEnabled) {
      return undefined; // No filtering if no allowedSubOrgIds or sub-orgs disabled
    }
    return allowedSubOrgIds;
  }, [allowedSubOrgIds, subOrganizationEnabled]);

  function reducer(state, action) {
    switch (action.type) {
      case 'start_searching':
        return { ...state, isSearchInProgress: true, searchValues: action.searchValues };
      case 'set_results':
        return { ...state, searchResultsDict: { ...state.searchResultsDict, [action.policyType]: action.results } };
      case 'search_finished':
        return { ...state, isSearchInProgress: false };
      case 'clean_search':
        return initialState;
      default:
        throw Error(`Dispatch unknown action type: ${action.type}`);
    }
  }

  const searchPolicies = async (url, params, policyType) => {
    try {
      if (isPolicySearchV2Enabled) {
        // The v2 search takes a body, so the flat query string splits into the two named
        // parameters and `fields`, which carries the organization's configured search fields. The
        // forwarded key set is the mechanical inverse of what the route used to derive from
        // request.args, so `fields` stays exactly the configured fields
        // validate_configured_fields_values accepts. sub_org_id is omitted rather than sent empty:
        // the form seeds it as '', which the route's `int | None` rejects.
        const { sub_org_id, lob, ...fields } = params;
        const res = await axios.post(url, {
          lob: policyType,
          sub_org_id: sub_org_id ? Number(sub_org_id) : undefined,
          fields,
        });
        dispatch({ type: 'set_results', policyType, results: res.data || {} });
      } else {
        const res = await axios.get(url, { params: { ...params, lob: policyType } });
        const searchPoliciesResults = res.data.map((policy) => policyPopulate(policy));
        dispatch({ type: 'set_results', policyType, results: searchPoliciesResults });
      }
    } catch (error) {
      dispatch({ type: 'set_results', policyType, results: isPolicySearchV2Enabled ? {} : [] });
      reportAxiosError(error);
    }
  };

  const searchDrafts = async (chosendPolicy) => {
    try {
      if (isPolicyAPIEnabled) {
        const res = await axios.get('/api/v1/fnol_drafts', {
          params: {
            policy_number: chosendPolicy.policy_number,
            date_of_loss: searchValues.date_of_loss,
            policy_lob: chosendPolicy.lob,
            sub_organization_id: subOrganizationEnabled ? chosendPolicy.sub_organization.id : undefined,
          },
        });
        return res.data;
      } else {
        const res = await axios.get(`/api/v1/claims/fnol/drafts/${chosendPolicy.id}`);
        if (res.data.id) {
          return [res.data];
        }
        return undefined;
      }
    } catch (error) {
      reportAxiosError(error);
    }
  };

  const reloadDrafts = async () => {
    let relatedDrafts = await searchDrafts(selectedPolicyContext?.enrichedPolicyData);
    setRelatedDrafts(relatedDrafts);
  };

  const handleSearch = async (searchValues) => {
    dispatch({ type: 'start_searching', searchValues });
    const chosenPolicyTypes = searchValues['lob'] ? [searchValues['lob'].replace('_claim', '_policy')] : policyTypes;
    const searchPolicyPromises = chosenPolicyTypes.map((policyType) => {
      let url = `/api/v1/${policyType.replace('_policy', '_policies')}`;
      if (isPolicyAPIEnabled) {
        url = '/api/v1/policies/get_policies_by_api';
      }

      if (isPolicySearchV2Enabled) {
        url = '/api/v1/policies/get_policies_by_api_v2';
      }

      return searchPolicies(url, searchValues, policyType);
    });
    await Promise.all(searchPolicyPromises);

    setSearchResultsLob(chosenPolicyTypes);
    dispatch({ type: 'search_finished' });
  };

  const handleSearchByAddress = async (searchValues) => {
    dispatch({ type: 'start_searching', searchValues });

    const searchPolicyPromises = policyTypes.map((policyType) => {
      searchPolicies(
        `/api/v1/${policyType.replace('_policy', '_policies')}/search_by_address`,
        { ...searchValues },
        policyType
      );
    });
    await Promise.all(searchPolicyPromises);

    dispatch({ type: 'search_finished' });
  };

  const handlePolicySelected = async (chosenPolicy) => {
    let chosenPolicyData = null;
    // at this point it's either actual policies or only a search result
    if (chosenPolicy.search_type) {
      if (chosenPolicy.search_type === POLICY_SEARCH_TYPES.DB) {
        try {
          const policyFetchRes = await axios.get(`/api/v1/policies/${chosenPolicy.id}`);
          chosenPolicyData = policyFetchRes.data;
        } catch (error) {
          reportAxiosError(error);
          return;
        }
      } else if (
        chosenPolicy.search_type === POLICY_SEARCH_TYPES.API_V1 ||
        chosenPolicy.search_type === POLICY_SEARCH_TYPES.API_V2
      ) {
        try {
          if (chosenPolicy.search_type === POLICY_SEARCH_TYPES.API_V2) {
            const policyFetchRes = await axios.post('/api/v1/policies/retrieve_policy_by_api_v2', {
              retrieval_token: chosenPolicy.retrieval_token,
            });
            chosenPolicyData = policyFetchRes.data;
          } else {
            const policyFetchRes = await axios.post(`/api/v1/policies/retrieve_policy_by_api`, chosenPolicy);
            chosenPolicyData = policyFetchRes.data;
          }
        } catch (error) {
          reportAxiosError(error);
          return;
        }
      } else {
        reportErrorInProductionOrThrow(`${chosenPolicy.search_type} is not a supported policy search type`);
      }
    } else {
      chosenPolicyData = chosenPolicy;
    }

    const subOrgId = userOrganization.sub_organizations_enabled ? chosenPolicyData?.sub_organization_id : null;
    if (subOrgId) {
      const subOrg = userOrganization.sub_organizations.find((sub_org) => sub_org.id === subOrgId);
      chosenPolicyData.sub_organization = subOrg;
    }

    const preCheck = await checkPolicyPreDefinedRequiredFields({ policyData: chosenPolicyData, subOrgId });
    if (preCheck.status === 'rejected') {
      return;
    }
    if (preCheck.status === 'ok' && preCheck.missing.length > 0) {
      const missingDescs = preCheck.missing.map((f) => f.desc).join(', ');
      toast.error(`Cannot select policy — missing required fields: ${missingDescs}`);
      return;
    }
    // 'transient' (5xx/network) falls through to the submit-time gate so a flaky pre-check
    // doesn't hard-block; the adjuster sees no failure toast while the select proceeds.

    if (enableSelectingRelatedClaim) {
      try {
        const claimsRes = await axios.get('/api/v1/claims', {
          params: {
            policy_id: chosenPolicyData.id,
            date_of_loss: searchValues?.date_of_loss || undefined,
            with_first_party_involved_entities: isConfigurableFnolEnabled && isCommercialPoliciesEnabled,
          },
        });
        const relatedClaims = claimsRes.data.claims;
        let relatedDrafts = await searchDrafts(chosenPolicyData);

        if (relatedClaims.length === 0 && !(relatedDrafts && relatedDrafts.length > 0)) {
          await handlePolicyFinallyChosen({ originalSearchResult: chosenPolicy, enrichedPolicyData: chosenPolicyData });
          return;
        }

        setRelatedDrafts(relatedDrafts);
        setRelatedClaims(relatedClaims);
        setSelectedPolicyContext({ originalSearchResult: chosenPolicy, enrichedPolicyData: chosenPolicyData });
      } catch (error) {
        reportAxiosError(error);
      }
    } else {
      await handlePolicyFinallyChosen({ originalSearchResult: chosenPolicy, enrichedPolicyData: chosenPolicyData });
    }
  };

  const handlePolicyFinallyChosen = async (policyContext) => {
    // Use original search result for FNOL Inbox, enriched data for other flows
    const policyToReturn = returnOriginalSearchResult
      ? policyContext.originalSearchResult
      : policyContext.enrichedPolicyData;

    await onSelectPolicy(policyToReturn, {
      date_of_loss: searchValues?.date_of_loss,
      reported_date: searchValues?.reported_date,
    });
    if (isMounted.current) {
      dispatch({ type: 'clean_search' });
    }
  };

  const handleCancelOrClaimLinkClick = () => {
    setRelatedClaims([]);
    setRelatedDrafts(undefined);
    setSelectedPolicyContext(undefined);
  };

  const hasSearchCompleted = !isSearchInProgress && !!searchResultsDict;
  const hasResults = hasSearchCompleted && Object.values(searchResultsDict).some((res) => res.length > 0);
  const hasNoResults = hasSearchCompleted && !hasResults;
  const showV1EmptyResultsWithoutPolicyButton = !isPolicySearchV2Enabled && hasNoResults && !!onTakeLossWithoutPolicy;

  // An organization with no policy API and no explicit legacy DB opt-in has no policy source:
  // its search box could only return an empty result set. The manual-policy affordance is the
  // caller's only fallback on the attach-to-policy screen, so it survives the hidden card.
  if (!hasPolicySource(policySearchConfig)) {
    // A method that is neither searchable nor the explicit `NONE` is a misconfiguration rather
    // than a deliberate absence of a source, and stays reportable. An absent config is neither:
    // the organization context has simply not resolved one yet.
    if (policySearchConfig?.method && policySearchConfig.method !== 'NONE') {
      reportErrorInProductionOrThrow(`Unknown policy search method: ${policySearchConfig.method}`);
    }
    if (!onSetManualPolicy) {
      return null;
    }
    return (
      <div className={styles.btnContainer}>
        <RestrictedPermissions action={PERMISSION_ACTIONS.MANUAL_POLICY} verb={PERMISSION_VERBS.WRITE}>
          <CancelButton
            classNameOverride={cn(styles.btn, styles.setPolicyBtn, styles.setPolicyBtnStandalone)}
            onClick={onSetManualPolicy}
            content="Set Policy Details Manually"
          />
        </RestrictedPermissions>
      </div>
    );
  }

  return (
    <>
      {isPolicySearchV2Enabled ? (
        <PolicySearchV2
          onSearch={handleSearch}
          initialSearchValues={initialSearchValues}
          constSearchValues={constSearchValues}
          isSearchInProgress={isSearchInProgress}
          disabled={disabled}
          hideSubOrg={hideSubOrg}
          hideLob={hideLob}
          onSetManualPolicy={onSetManualPolicy}
          includedSubOrganizationsIds={includedSubOrganizationsIds}
          hideCollapsibleWrapper={hideCollapsibleWrapper}
        />
      ) : (
        <PolicySearch
          onSearch={handleSearch}
          constSearchValues={constSearchValues}
          initialSearchValues={initialSearchValues}
          onSearchByAddress={allowSearchByAddress ? () => setShowSearchByAddressDialog(true) : undefined}
          policySearchConfig={policySearchConfig}
          onCouldntFindPolicy={onPolicyNotFound}
          policyTypes={policyTypes}
          isSearchInProgress={isSearchInProgress}
          errorMsg={
            (hasNoResults && 'No Policies Found. Please make sure the policy number and the DOL are correct') || ''
          }
          disabled={disabled}
          hideSubOrg={hideSubOrg}
          hideDateOfLoss={hideDateOfLoss}
          hideLob={hideLob}
          newDesign={newDesign}
          onSetManualPolicy={onSetManualPolicy}
          includedSubOrganizationsIds={includedSubOrganizationsIds}
          hideCollapsibleWrapper={hideCollapsibleWrapper}
        />
      )}
      {isPolicySearchV2Enabled && hasSearchCompleted ? (
        <PolicySearchResultsV2
          searchPoliciesResults={searchResultsDict}
          onCancel={() => dispatch({ type: 'clean_search' })}
          onSelectPolicy={handlePolicySelected}
          onCouldntFindPolicy={onPolicyNotFound}
          policyTypes={searchResultsLob}
          selectButtonText={selectButtonText}
          disableSelectButton={disableSelectButton}
          onTakeLossWithoutPolicy={onTakeLossWithoutPolicy}
        />
      ) : null}
      {!isPolicySearchV2Enabled && hasResults ? (
        <PolicySearchResults
          classes={classes}
          searchPoliciesResults={searchResultsDict}
          onCancel={() => dispatch({ type: 'clean_search' })}
          onSelectPolicy={handlePolicySelected}
          onCouldntFindPolicy={onPolicyNotFound}
          policyTypes={searchResultsLob}
          selectButtonText={selectButtonText}
          disableSelectButton={disableSelectButton}
          onTakeLossWithoutPolicy={onTakeLossWithoutPolicy}
        />
      ) : null}
      {showV1EmptyResultsWithoutPolicyButton ? (
        <div className="mt-16 flex justify-end">
          <CancelButton content="Take Loss Without Policy" onClick={onTakeLossWithoutPolicy} withMarginRight={false} />
        </div>
      ) : null}
      {enableSelectingRelatedClaim &&
        ((relatedClaims && relatedClaims.length > 0) || (relatedDrafts && relatedDrafts.length > 0)) && (
          <ClaimSearchResults
            additionalComponent={
              <>
                {onFnolDraftSelected && (
                  <div className={classes.cardDivRowInternal}>
                    {relatedDrafts && relatedDrafts.length > 0 ? (
                      <CardDialog title="Saved Drafts">
                        <FnolDraftsTable
                          drafts={relatedDrafts}
                          onSelectDraft={async (draft) => await onFnolDraftSelected(draft)}
                          onDraftDiscarded={reloadDrafts}
                        />
                      </CardDialog>
                    ) : (
                      <Typography>No related FNOL drafts found </Typography>
                    )}
                  </div>
                )}
                <div className={classes.buttonsContainer}>
                  <Button
                    disabled={reltadClaimsSubmitting}
                    onClick={async () => {
                      setReltadClaimsSubmitting(true);
                      try {
                        await handlePolicyFinallyChosen(selectedPolicyContext);
                      } catch (error) {
                        reportAxiosError(error);
                      } finally {
                        setReltadClaimsSubmitting(false);
                      }
                    }}
                    variant="contained"
                    color="primary"
                  >
                    {choosePolicyFromRelatedClaimsText}
                  </Button>
                </div>
              </>
            }
            preResultsTableComponent={
              relatedClaims.length === CONSTANTS.DEFAULT_CLAIMS_PER_PAGE && (
                <div className={classes.cardDivRow}>
                  <span
                    className={classes.textFieldRow}
                  >{`Showing only the first ${CONSTANTS.DEFAULT_CLAIMS_PER_PAGE} results. Refine your search query to find other claims or use the All Claims view`}</span>
                </div>
              )
            }
            classes={classes}
            title="Existing claims for selected policy"
            searchResults={relatedClaims}
            SelectComponent={selectRelatedClaimComponent}
            onCancel={handleCancelOrClaimLinkClick}
            onClaimLinkClick={handleCancelOrClaimLinkClick}
            hideClaimType
          />
        )}
      {showSearchByAddressDialog && (
        <SearchByAddressDialog
          policySearchConfig={policySearchConfig}
          initialSearchValues={initialSearchValues}
          onSearch={handleSearchByAddress}
          onClose={() => setShowSearchByAddressDialog(false)}
        />
      )}
    </>
  );
}

PolicySearchContainer.propTypes = {
  constSearchValues: PropTypes.object,
  initialSearchValues: PropTypes.object,
  onSelectPolicy: PropTypes.func.isRequired,
  enableSelectingRelatedClaim: PropTypes.bool,
  choosePolicyFromRelatedClaimsText: requiredIf(PropTypes.string, (props) => props.enableSelectingRelatedClaim),
  selectRelatedClaimComponent: PropTypes.func,
  onPolicyNotFound: PropTypes.func,
  disabled: PropTypes.bool,
  policyTypes: PropTypes.array.isRequired,
  selectButtonText: PropTypes.string,
  disableSelectButton: PropTypes.bool,
  onFnolDraftSelected: requiredIf(PropTypes.func, (props) => props.enableSelectingRelatedClaim),
  allowSearchByAddress: PropTypes.bool,
  hideSubOrg: PropTypes.bool,
  hideDateOfLoss: PropTypes.bool,
  hideLob: PropTypes.bool,
  newDesign: PropTypes.bool,
  onSetManualPolicy: PropTypes.func,
  allowedSubOrgIds: PropTypes.array,
  hideCollapsibleWrapper: PropTypes.bool,
  returnOriginalSearchResult: PropTypes.bool,
  onTakeLossWithoutPolicy: PropTypes.func,
};

export default PolicySearchContainer;
